import React from 'react';
import { i18n } from 'config/locales';
import { LEGAL, LEGAL_ASSOCIATIONS_VIOLENT_ACTS } from 'config/formSections/legal';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import BranchCollection from 'components/Form/BranchCollection';
import { LegalAssociationsViolentActsModel } from 'forms/sections/legal/associations/violentActs/types';
import { EmptyBranchCollectionInstance } from 'forms/shared/validators/models/branchCollection/instance';
import EventItem from './EventItem';

const sectionConfig = {
  key: LEGAL_ASSOCIATIONS_VIOLENT_ACTS.key,
  section: LEGAL.name,
  store: LEGAL.store,
  subsection: LEGAL_ASSOCIATIONS_VIOLENT_ACTS.name,
  storeKey: LEGAL_ASSOCIATIONS_VIOLENT_ACTS.storeKey,
};

export class ViolentActs extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue: Partial<LegalAssociationsViolentActsModel>) => {
    this.props.onUpdate(this.storeKey, {
      HasViolentAct: this.props.HasViolentAct,
      GovermentOfficials: this.props.GovermentOfficials,
      HateCrimes: this.props.HateCrimes,
      GovernmentProperty: this.props.GovernmentProperty,
      ...queue,
    });
  };

  updateBranch = (values: LegalAssociationsViolentActsModel['HasViolentAct']) => {
    this.update({
      HasViolentAct: values,
      HateCrimes: values.value === 'Yes' ? this.props.HateCrimes : EmptyBranchCollectionInstance,
      GovernmentProperty: values.value === 'Yes' ? this.props.GovernmentProperty : EmptyBranchCollectionInstance,
      GovermentOfficials: values.value === 'Yes' ? this.props.GovermentOfficials : EmptyBranchCollectionInstance,
    });
  };

  updateHateCrimes = (values: LegalAssociationsViolentActsModel['HateCrimes']) => {
    this.update({
      HateCrimes: values,
    });
  };

  updateGovernmentProperty = (values: LegalAssociationsViolentActsModel['GovernmentProperty']) => {
    this.update({
      GovernmentProperty: values,
    });
  };

  updateGovermentOfficials = (values: LegalAssociationsViolentActsModel['GovermentOfficials']) => {
    this.update({
      GovermentOfficials: values,
    });
  };

  render() {
    const subSectionName = 'violentActs';
    const sectionAssetPath = 'legal.pvq.associations';
    const subSectionAssetPath = `${sectionAssetPath}.${subSectionName}`;
    const subSectionClassName = subSectionAssetPath.replaceAll('.', '-');

    return (
      <div
        className={`section-content ${subSectionClassName}`}
        data-section={LEGAL.key}
        data-subsection={LEGAL_ASSOCIATIONS_VIOLENT_ACTS.key}
      >
        <h1 className="section-header">
          {i18n.t(`legal.pvq.subsection.associations.${subSectionName}`)}
        </h1>
        <Branch
          name="HasViolentAct"
          label={i18n.t(`${subSectionAssetPath}.hasViolentAct`)}
          labelSize="h4"
          className={`${subSectionClassName}-HasViolentAct`}
          {...this.props.HasViolentAct}
          warning
          onError={this.handleError}
          required={this.props.required}
          onUpdate={this.updateBranch}
          warning
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasViolentAct"
        />

        <Show when={this.props.HasViolentAct.value === 'Yes'}>
          <BranchCollection
            name="HateCrimes"
            attribute="HateCrimes"
            label={i18n.t(`${subSectionAssetPath}.wasHateCrime`)}
            labelSize="h4"
            appendLabel={i18n.t(`${subSectionAssetPath}.wasHateCrimeAppend`)}
            className={`${subSectionClassName}-wasHateCrime`}
            {...this.props.HateCrimes}
            warning
            onError={this.handleError}
            required={this.props.required}
            onUpdate={this.updateHateCrimes}
            scrollIntoView={this.props.scrollIntoView}
          >
            <EventItem
              name="Item"
              attribute="HateCrimes"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              subSectionAssetPath={`${subSectionAssetPath}.hateCrime`}
              subSectionClassName={`${subSectionAssetPath}-hateCrime`}
              hasWhy
              hasWhen
              hasWhat
            />
          </BranchCollection>
          <BranchCollection
            name="GovermentOfficials"
            attribute="GovermentOfficials"
            label={i18n.t(`${subSectionAssetPath}.wasGovermentOfficial`)}
            labelSize="h4"
            appendLabel={i18n.t(`${subSectionAssetPath}.wasGovermentOfficialAppend`)}
            className={`${subSectionClassName}-wasGovermentOfficial`}
            {...this.props.GovermentOfficials}
            warning
            onError={this.handleError}
            required={this.props.required}
            onUpdate={this.updateGovermentOfficials}
            scrollIntoView={this.props.scrollIntoView}
          >
            <EventItem
              name="Item"
              attribute="GovermentOfficials"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              subSectionAssetPath={`${subSectionAssetPath}.govermentOfficial`}
              subSectionClassName={`${subSectionAssetPath}-govermentOfficial`}
              hasWhy
              hasWhen
              hasWhat
            />
          </BranchCollection>
          <BranchCollection
            name="GovernmentProperty"
            attribute="GovernmentProperty"
            label={i18n.t(`${subSectionAssetPath}.wasGovernmentProperty`)}
            labelSize="h4"
            appendLabel={i18n.t(`${subSectionAssetPath}.wasGovernmentPropertyAppend`)}
            className={`${subSectionClassName}-wasGovernmentProperty`}
            {...this.props.GovernmentProperty}
            warning
            onError={this.handleError}
            required={this.props.required}
            onUpdate={this.updateGovernmentProperty}
            scrollIntoView={this.props.scrollIntoView}
          >
            <EventItem
              name="Item"
              attribute="GovernmentProperty"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              subSectionAssetPath={`${subSectionAssetPath}.governmentProperty`}
              subSectionClassName={`${subSectionAssetPath}-governmentProperty`}
              hasWas
              hasWhy
              hasWhen
              hasWhat
            />
          </BranchCollection>
        </Show>
      </div>
    );
  }
}

ViolentActs.defaultProps = {
  name: 'violentActs',
  HasViolentAct: {},
  HateCrimes: EmptyBranchCollectionInstance,
  GovermentOfficials: EmptyBranchCollectionInstance,
  GovernmentProperty: EmptyBranchCollectionInstance,
  defaultState: true,
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'legal',
  subsection: 'associations/violent-acts',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(ViolentActs, sectionConfig);
